import type { CSSProperties } from "react";
import { useTime } from "../common/core.js";

/**
 * Cipher Crack — resolution overlays.
 *
 *  CrackedBanner — gold + obsidian, big multiplier, code reveal,
 *                  payout chip, drifting gold dust.
 *  SealedBanner  — red strobe, code reveal, bust banner.
 *
 * Multiplier fonts shrunk vs the design (132 → 96, 92 → 72) so the cards
 * fit within a 1280×720 main column without overflowing.
 */

const baseWrap: CSSProperties = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  zIndex: 40,
};

const baseCard: CSSProperties = {
  position: "absolute",
  left: "50%",
  top: "48%",
  transform: "translate(-50%, -50%)",
  textAlign: "center",
  pointerEvents: "auto",
  padding: "20px 32px",
  borderRadius: 3,
  minWidth: 380,
  animation: "pp-bump 360ms cubic-bezier(.2,.8,.3,1.15)",
};

export function CrackedBanner({
  attempt,
  multiplier,
  payout,
  code,
  currency = "BTC",
}: {
  attempt: number;
  multiplier: number;
  payout: number;
  code: ReadonlyArray<number>;
  currency?: string;
}) {
  const t = useTime();
  return (
    <div style={baseWrap}>
      {/* Warm overhead light wash */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(255,232,168,0.30) 0%, rgba(241,203,107,0.13) 28%, transparent 60%)",
          mixBlendMode: "screen",
        }}
      />
      <div
        style={{
          ...baseCard,
          background:
            "linear-gradient(180deg, rgba(36,27,12,0.92) 0%, rgba(11,14,22,0.95) 100%)",
          border: "1px solid var(--gold)",
          boxShadow:
            "0 0 60px rgba(241,203,107,0.35), 0 24px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,232,168,0.1)",
        }}
      >
        {/* Header pill */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 12px",
            background: "rgba(91,208,138,0.12)",
            border: "1px solid rgba(91,208,138,0.45)",
            borderRadius: 2,
            fontFamily: "var(--font-ui)",
            fontSize: 9,
            fontWeight: 800,
            color: "var(--green-bright)",
            letterSpacing: 3,
            textTransform: "uppercase",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "var(--green-bright)",
              boxShadow: "0 0 8px var(--green)",
            }}
          />
          Vault Cracked · Attempt 0{attempt}
        </div>
        {/* Big multiplier */}
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 96,
            color: "var(--gold-bright)",
            letterSpacing: -3,
            lineHeight: 0.85,
            marginTop: 12,
            textShadow: "0 0 22px var(--gold), 0 0 48px var(--gold-deep)",
          }}
        >
          {multiplier.toFixed(2)}
          <span style={{ fontSize: 56 }}>×</span>
        </div>
        {/* Payout chip */}
        <div
          style={{
            display: "inline-flex",
            gap: 10,
            alignItems: "baseline",
            padding: "6px 16px",
            marginTop: 6,
            background: "rgba(91,208,138,0.10)",
            border: "1px solid rgba(91,208,138,0.45)",
            borderRadius: 2,
            fontFamily: "var(--font-mono)",
            fontSize: 14,
            color: "var(--green-bright)",
            fontWeight: 600,
          }}
        >
          +{payout.toFixed(8)} {currency}
        </div>
        {/* Divider */}
        <div
          style={{
            margin: "16px auto 12px",
            width: 80,
            height: 1,
            background:
              "linear-gradient(90deg, transparent, var(--gold), transparent)",
          }}
        />
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: 3,
            color: "var(--fg-3)",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          The combination was
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
          {code.map((d, i) => (
            <div
              key={i}
              style={{
                width: 38,
                height: 50,
                background: "linear-gradient(180deg, #231C0E 0%, #120D04 100%)",
                border: "1px solid var(--gold-bright)",
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-display)",
                fontSize: 30,
                color: "#FFE8A8",
                letterSpacing: -1,
                textShadow: "0 0 12px var(--gold-bright)",
                boxShadow:
                  "inset 0 0 14px rgba(241,203,107,0.2), 0 0 16px rgba(241,203,107,0.3)",
              }}
            >
              {d}
            </div>
          ))}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--fg-3)",
            marginTop: 14,
            letterSpacing: 0.5,
          }}
        >
          server seed reveals on resolve
        </div>
      </div>
      {/* Drifting gold dust upward */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {Array.from({ length: 14 }).map((_, i) => {
          const seed = i * 41;
          const ph = ((t * 0.35) + seed * 0.06) % 1;
          const x = 12 + ((seed * 17) % 76);
          const y = 100 - ph * 90;
          const op = (1 - ph) * 0.7;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: `${x}%`,
                top: `${y}%`,
                width: 2.5,
                height: 2.5,
                borderRadius: "50%",
                background: "var(--gold-bright)",
                opacity: op,
                boxShadow: "0 0 5px var(--gold-bright)",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export function CashedOutBanner({
  multiplier,
  payout,
  attemptCashedAt,
  code,
  currency = "BTC",
}: {
  /** Cashout multiplier locked in (e.g. 0.55, 1.05). */
  multiplier: number;
  /** Total return = stake × multiplier. May be < stake (loss) or > stake (small profit). */
  payout: number;
  /** 1-based attempt index the player was ON when they cashed out. */
  attemptCashedAt: number;
  /** Revealed code (post-resolve). */
  code: ReadonlyArray<number>;
  currency?: string;
}) {
  const profit = payout - payout / multiplier; // = stake * (mult - 1)
  const stake = payout / Math.max(multiplier, 0.0001);
  const isProfit = multiplier >= 1;
  const accent = isProfit ? "var(--green-bright)" : "var(--cyan-bright)";
  const accentDeep = isProfit ? "var(--green)" : "var(--cyan)";
  return (
    <div style={baseWrap}>
      {/* Cool cyan light wash */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(107,198,224,0.25) 0%, rgba(107,198,224,0.10) 28%, transparent 60%)",
          mixBlendMode: "screen",
        }}
      />
      <div
        style={{
          ...baseCard,
          background:
            "linear-gradient(180deg, rgba(12,28,36,0.92) 0%, rgba(11,14,22,0.95) 100%)",
          border: "1px solid var(--cyan)",
          boxShadow:
            "0 0 60px rgba(107,198,224,0.30), 0 24px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(155,224,242,0.1)",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 12px",
            background: "rgba(107,198,224,0.12)",
            border: "1px solid rgba(107,198,224,0.5)",
            borderRadius: 2,
            fontFamily: "var(--font-ui)",
            fontSize: 9,
            fontWeight: 800,
            color: accent,
            letterSpacing: 3,
            textTransform: "uppercase",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: accent,
              boxShadow: `0 0 8px ${accentDeep}`,
            }}
          />
          Cashed Out · Attempt 0{attemptCashedAt}
        </div>
        {/* Multiplier */}
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 84,
            color: accent,
            letterSpacing: -2,
            lineHeight: 0.9,
            marginTop: 12,
            textShadow: `0 0 18px ${accentDeep}`,
          }}
        >
          {multiplier.toFixed(2)}
          <span style={{ fontSize: 50 }}>×</span>
        </div>
        {/* Payout chip */}
        <div
          style={{
            display: "inline-flex",
            padding: "6px 16px",
            marginTop: 4,
            background: isProfit
              ? "rgba(91,208,138,0.10)"
              : "rgba(107,198,224,0.10)",
            border: `1px solid ${isProfit ? "rgba(91,208,138,0.45)" : "rgba(107,198,224,0.45)"}`,
            borderRadius: 2,
            fontFamily: "var(--font-mono)",
            fontSize: 14,
            color: accent,
            fontWeight: 600,
          }}
        >
          {isProfit ? "+" : "−"}
          {Math.abs(profit).toFixed(8)} {currency}
        </div>
        {/* Divider */}
        <div
          style={{
            margin: "16px auto 12px",
            width: 80,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${accentDeep}, transparent)`,
          }}
        />
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: 3,
            color: "var(--fg-3)",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          The combination was
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
          {code.map((d, i) => (
            <div
              key={i}
              style={{
                width: 36,
                height: 48,
                background: "linear-gradient(180deg, #122027 0%, #08111A 100%)",
                border: `1px solid ${accentDeep}`,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-display)",
                fontSize: 28,
                color: accent,
                letterSpacing: -1,
                textShadow: `0 0 8px ${accentDeep}`,
              }}
            >
              {d}
            </div>
          ))}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--fg-3)",
            marginTop: 14,
            letterSpacing: 0.5,
          }}
        >
          stake {stake.toFixed(8)} {currency} · seed reveals
        </div>
      </div>
    </div>
  );
}

export function SealedBanner({
  code,
  stake,
  currency = "BTC",
}: {
  code: ReadonlyArray<number>;
  stake: number;
  currency?: string;
}) {
  const t = useTime();
  const strobe = Math.sin(t * 11) > 0.5 ? 1 : 0.35;
  return (
    <div style={baseWrap}>
      {/* Edge red strobe */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          boxShadow: `inset 0 0 ${50 + strobe * 60}px rgba(224,69,69,${0.32 + strobe * 0.4})`,
          opacity: strobe,
          transition: "opacity 80ms",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(224,69,69,0.18), transparent 55%)",
        }}
      />
      <div
        style={{
          ...baseCard,
          background:
            "linear-gradient(180deg, rgba(40,12,12,0.92) 0%, rgba(11,14,22,0.95) 100%)",
          border: "1px solid var(--red)",
          boxShadow:
            "0 0 60px rgba(224,69,69,0.4), 0 24px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,106,106,0.12)",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 12px",
            background: "rgba(224,69,69,0.14)",
            border: "1px solid rgba(224,69,69,0.5)",
            borderRadius: 2,
            fontFamily: "var(--font-ui)",
            fontSize: 9,
            fontWeight: 800,
            color: "var(--red-bright)",
            letterSpacing: 3,
            textTransform: "uppercase",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "var(--red-bright)",
              boxShadow: "0 0 8px var(--red-bright)",
              opacity: strobe,
            }}
          />
          Vault Sealed
        </div>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 72,
            color: "var(--red-bright)",
            letterSpacing: -2,
            lineHeight: 0.9,
            marginTop: 12,
            textShadow: "0 0 18px var(--red), 0 0 36px var(--red-deep, #6A1A1A)",
          }}
        >
          0.00<span style={{ fontSize: 44 }}>×</span>
        </div>
        <div
          style={{
            display: "inline-flex",
            padding: "6px 16px",
            marginTop: 4,
            background: "rgba(224,69,69,0.10)",
            border: "1px solid rgba(224,69,69,0.4)",
            borderRadius: 2,
            fontFamily: "var(--font-mono)",
            fontSize: 14,
            color: "var(--red-bright)",
            fontWeight: 600,
          }}
        >
          −{stake.toFixed(8)} {currency}
        </div>
        <div
          style={{
            margin: "16px auto 12px",
            width: 80,
            height: 1,
            background: "linear-gradient(90deg, transparent, var(--red), transparent)",
          }}
        />
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: 3,
            color: "var(--fg-3)",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          The combination was
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
          {code.map((d, i) => (
            <div
              key={i}
              style={{
                width: 38,
                height: 50,
                background: "linear-gradient(180deg, #1E1216 0%, #0A0608 100%)",
                border: "1px solid rgba(224,69,69,0.55)",
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-display)",
                fontSize: 30,
                color: "var(--red-bright)",
                letterSpacing: -1,
                textShadow: "0 0 10px var(--red)",
              }}
            >
              {d}
            </div>
          ))}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--fg-3)",
            marginTop: 14,
            letterSpacing: 0.5,
          }}
        >
          attempts spent · seed reveals
        </div>
      </div>
    </div>
  );
}
